import { AUTH_SUBS_BASE_URL } from "src/configs/config";
import {
  LOGIN_ROUTE,
  SUBSCRIPTION_ROUTE,
  isAuthRecoveryPathname,
} from "src/routePaths";

const currentOrigin = window.location.origin;

export type ApiError = Error & {
  status?: number;
  body?: { message?: string | string[] };
};

const authHeaders = (locale: string) => ({
  authorization: `Bearer ${window.localStorage.getItem("__sal_auth") || ""}`,
  "x-localization": locale,
  origin: currentOrigin,
});

const handleRes = async (res: Response) => {
  if ([500, 503].includes(res.status)) return undefined;

  if (res.status === 401) {
    localStorage.removeItem("__sal_auth");
    localStorage.removeItem("__sal_auth_refresh");
    localStorage.removeItem("userEmail");
    if (!isAuthRecoveryPathname(window.location.pathname)) {
      window.location.replace(LOGIN_ROUTE);
    }
  }

  const path = window.location.pathname;

  if (res.status === 403) {
    try {
      const { cause } = await res.json();
      if (!res.url.includes("/playlist") && !path.includes("content/playlists")) {
        if (cause === "subscription") {
          if (window.location.pathname !== SUBSCRIPTION_ROUTE) {
            window.location.replace(SUBSCRIPTION_ROUTE);
          }
        }
      }
    } catch {
      // ignore parse errors
    }
    return undefined;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 409) {
      return body;
    }
    const error: ApiError = new Error(
      body.message || `Request failed with status ${res.status}`,
    );
    error.status = res.status;
    error.body = body;
    return Promise.reject(error);
  }

  try {
    const jsonRes = await res.json();
    if (jsonRes.affected) {
      return Promise.resolve({ success: true });
    }
    return jsonRes;
  } catch {
    return undefined;
  }
};

export const authSubsGet = <T>(
  endpoint: string,
  locale: string = localStorage.getItem("locale") || "en",
): Promise<T> =>
  fetch(`${AUTH_SUBS_BASE_URL}${endpoint}`, {
    headers: authHeaders(locale),
  }).then(handleRes);

export const authSubsPost = <T>(
  endpoint: string,
  body: unknown,
  locale: string = localStorage.getItem("locale") || "en",
): Promise<T> =>
  fetch(`${AUTH_SUBS_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      ...authHeaders(locale),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  }).then(handleRes);

export const authSubsPut = <T>(
  endpoint: string,
  body: unknown,
  locale: string = localStorage.getItem("locale") || "en",
): Promise<T> =>
  fetch(`${AUTH_SUBS_BASE_URL}${endpoint}`, {
    method: "PUT",
    headers: {
      ...authHeaders(locale),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  }).then(handleRes);
